import React, { createContext, useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { asyncWithLDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk'
import { get as getConfig } from 'config'

const FeatureFlagsContext = createContext({})

export const useFeatureFlags = () => useContext(FeatureFlagsContext)

const FlagsBridge = ({ children }) => {
  const flags = useFlags()
  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

const LDContextSync = () => {
  const ldClient = useLDClient()
  const identity = useSelector(state => state.identity)

  useEffect(() => {
    if (!ldClient) return

    const context = identity.signedIn
      ? {
          kind: 'user',
          key: String(identity.userId),
          email: identity.email,
          candidateId: identity.candidateId,
          employerUserId: identity.employerUserId,
        }
      : { kind: 'user', key: 'anonymous', anonymous: true }

    ldClient.identify(context).catch(err =>
      console.error('Failed to update LaunchDarkly context:', err),
    )
  }, [ldClient, identity.signedIn, identity.userId])

  return null
}

const withTimeout = (promise, ms) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error('LaunchDarkly init timed out')), ms),
    ),
  ])

export const initializeLaunchDarkly = async () => {
  const clientId = getConfig('launchDarklyClientId', null)

  if (!clientId) {
    console.warn('LaunchDarkly client ID not configured. Feature flags will use defaults.')
    return ({ children }) => <>{children}</>
  }

  try {
    const LDProvider = await withTimeout(
      asyncWithLDProvider({
        clientSideID: clientId,
        context: { kind: 'user', key: 'anonymous', anonymous: true },
        options: {
          streaming: true,
          sendEvents: true,
          bootstrap: 'localStorage',
        },
      }),
      5000,
    )

    return ({ children }) => (
      <LDProvider>
        <FlagsBridge>
          <LDContextSync />
          {children}
        </FlagsBridge>
      </LDProvider>
    )
  } catch (error) {
    console.error('Failed to initialize LaunchDarkly:', error)
    return ({ children }) => <>{children}</>
  }
}
